import { markRaw } from "vue"

export default {
    data() {
        return {
            panels: {},
        }
    },

    provide() {
        return {
            pushSidePanel: (name, cmp, options) =>
                this.pushSidePanel(name, cmp, options),
            popSidePanel: name => this.popSidePanel(name),
            toggleSidePanel: (name, cmp, options) =>
                this.toggleSidePanel(name, cmp, options),
        }
    },

    methods: {
        /**
         * Show a `component` as a side panel.
         * There can only be one `component` for each `panelName`.
         *
         * * props:          Props to pass to the component.
         * * on:             Listeners to pass to the component.
         * * enterFrom:      Which side of the window to show the panel at.
         *                   Valid values: "right" | "left" | "bottom" | "top".
         *                   Defaults to: "right".
         * * focus:          Give focus to the panel, keep it inside and restore it when closed.
         * * blockScroll:    Whether to block the scroll on the html element.
         * * overlayClass:   A class to apply to the ModalLayer surrounding the panel.
         * * onClickOutside: A function to be called when clicking outside the panel.
         *                   Defaults to removing the panel.
         *                   You probably want to use `onClose`.
         * * onEscape:       A function to be called when pressing Escape.
         *                   Defaults to removing the panel.
         *                   You probably want to use `onClose`.
         * * onClose:        A function to be called when the panel is closed.
         * * fullScreen:     A falsy value, or a function returning a boolean.
         *                   If the functions returns true, show the panel above the main header.
         * * skipTrappingFocusWhenThisElementGetsFocus:
         *                   Don't run the focus trapping logic if the focus goes to this element.
         *                   You probably don't want this.
         *                   Consult the team if you're going to use it.
         *
         * returns: A function that removes the panel when called.
         */
        pushSidePanel(
            panelName,
            component,
            {
                props = {},
                on = {},
                enterFrom = "right",
                focus = true,
                blockScroll = true,
                overlayClass = "bg--black--50",
                skipTrappingFocusWhenThisElementGetsFocus,
                onClickOutside,
                onEscape,
                onClose,
                fullScreen = false,
            } = {}
        ) {
            const panelInfo = {
                restoreFocusEl: document.activeElement,
                component: markRaw(component),
                props,
                fullScreen,
                blockScroll,
                enterFrom,
                on,
                focus,
                overlayClass,
                skipTrappingFocusWhenThisElementGetsFocus,
                onClickOutside:
                    onClickOutside ??
                    (event => {
                        event.preventDefault()
                        event.stopPropagation()
                        this.popSidePanel(panelName)
                    }),
                onEscape:
                    onEscape ??
                    (event => {
                        event.stopPropagation()
                        this.popSidePanel(panelName)
                    }),
                onClose,
            }
            this.panels[panelName] = panelInfo
            return () => this.popSidePanel(panelName)
        },

        /**
         * Removes a side panel of the given `panelName`, if any.
         */
        popSidePanel(panelName) {
            const panel = this.panels[panelName]
            if (!panel) return
            delete this.panels[panelName]
            panel.onClose?.()
            if (panel.focus) {
                this.$nextTick(() => {
                    // Do this after the panel has been destroyed,
                    // so its focus handler doesn't kick in.
                    panel.restoreFocusEl?.focus()
                })
            }
        },

        toggleSidePanel(panelName, component, options) {
            if (panelName in this.panels) {
                this.popSidePanel(panelName)
            } else {
                this.pushSidePanel(panelName, component, options)
            }
        },
    },
}
